import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { calcDiscount, CURRENCY_LABEL, getBaseCost, getWeeklyPrice } from '@/helpers/payments';
import { cn } from '@/lib/utils';
import { SubscriptionType, type SubscriptionPlanDto } from '@editar-pdf/api';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

/** The order the plans are offered in, cheapest commitment first. */
const TARIFF_TYPES = [SubscriptionType.TRIAL, SubscriptionType.MONTHLY, SubscriptionType.YEARLY];

interface PlanPickerProps {
  tariff: SubscriptionType;
  plans?: SubscriptionPlanDto[];
  onTariffChange: (tariff: SubscriptionType) => void;
  disabled?: boolean;
}

/**
 * The paywall's plan selector: one radio row per purchasable plan, name and any discount on
 * the left, the weekly price in mono on the right, which is what makes monthly and yearly
 * comparable at a glance. What each plan really charges is spelled out in the BillingSummary
 * directly beneath the group. The selected row takes a teal border and the accent wash (§2),
 * which is the same "here, now, yours" marker the editor uses.
 */
const PlanPicker = ({ tariff, plans, onTariffChange, disabled = false }: PlanPickerProps) => {
  const { t } = useTranslation('web');
  const groupId = useId();

  const rows = TARIFF_TYPES.map((type) => ({ type, plan: plans?.find((v) => v.subscriptionType === type) })).filter(
    (row): row is { type: SubscriptionType; plan: SubscriptionPlanDto } => Boolean(row.plan),
  );

  return (
    <RadioGroup
      value={tariff}
      onValueChange={(value) => onTariffChange(value as SubscriptionType)}
      disabled={disabled}
      aria-label={t('components.paymentModal.title', 'Choose a plan')}
      className="gap-2"
    >
      {rows.map(({ type, plan }) => {
        const selected = type === tariff;
        const discount = calcDiscount(type, plans);
        const itemId = `${groupId}-${type}`;
        // The trial has no weekly rate to quote: it is three fixed days, so it shows its charge.
        const weekly = getWeeklyPrice(plan);
        const price =
          weekly === undefined ? `${CURRENCY_LABEL}${getBaseCost(plan)}` : `${CURRENCY_LABEL}${weekly.toFixed(2)}`;

        return (
          <label
            key={type}
            htmlFor={itemId}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-(--animate-duration-hover) ease-(--ease-standard)',
              selected ? 'border-primary bg-accent-wash' : 'border-border bg-card hover:border-border-strong',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <RadioGroupItem
              id={itemId}
              value={type}
              // The visible row is the label, so the accessible name has to come from the
              // control itself — a `<label>` wrapping both would name it twice.
              aria-label={
                weekly === undefined
                  ? `${t(`tariffs.names.${type}`)} ${price}`
                  : `${t(`tariffs.names.${type}`)} ${price} / ${t('tariffs.period.WEEK')}`
              }
            />
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="text-ui text-ink">{t(`tariffs.names.${type}`)}</span>
              {discount ? <Badge>-{discount}%</Badge> : null}
            </span>
            <span className="shrink-0 font-mono text-[13px] text-ink-2">
              {price}
              {weekly === undefined ? null : <span className="text-subtle">/{t('tariffs.period.WEEK')}</span>}
            </span>
          </label>
        );
      })}
    </RadioGroup>
  );
};

export { PlanPicker };
